import { useEffect, useRef, useState } from 'react'
import './web-test-how.css'

function StrategyMini() {
  return (
    <div className="wh-illust wh-illust--strategy" aria-hidden="true">
      <span className="wh-pill">SAFE</span>
      <span className="wh-pill wh-pill--on">BALANCE</span>
      <span className="wh-pill">AGGRESSIVE</span>
    </div>
  )
}

function DirectionMini() {
  return (
    <div className="wh-illust wh-illust--direction" aria-hidden="true">
      <span className="wh-dir wh-dir--buy">BUY</span>
      <span className="wh-dir wh-dir--sell">SELL</span>
    </div>
  )
}

function ExecutionMini() {
  return (
    <div className="wh-illust wh-illust--exec" aria-hidden="true">
      <div className="wh-mt5">
        <span className="wh-mt5__mark">MT5</span>
        <span className="wh-mt5__line" />
        <span className="wh-mt5__dot" />
      </div>
    </div>
  )
}

function OutcomeMini() {
  return (
    <div className="wh-illust wh-illust--outcome" aria-hidden="true">
      <div className="wh-outcome-row">
        <span>Target</span>
        <strong>✓</strong>
      </div>
      <div className="wh-outcome-row">
        <span>Stop</span>
        <strong>—</strong>
      </div>
    </div>
  )
}

const STEPS = [
  {
    id: 'strategy',
    title: 'Choose Strategy',
    body: 'Select a predefined strategy profile for the prediction market.',
    Illustration: StrategyMini,
  },
  {
    id: 'direction',
    title: 'Choose Direction',
    body: 'You select BUY or SELL. Perfect Predict does not choose direction for you.',
    Illustration: DirectionMini,
  },
  {
    id: 'execution',
    title: 'Trade Executes',
    body: 'The trade executes through MetaTrader 5 infrastructure.',
    Illustration: ExecutionMini,
  },
  {
    id: 'outcome',
    title: 'Target or Stop Determines the Result',
    body: "The outcome is set by the selected strategy's predefined target or stop.",
    Illustration: OutcomeMini,
  },
] as const

export function WebTestHowItWorks() {
  const rootRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0.18 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section
      ref={rootRef}
      id="how-it-works"
      className={`wh-section${visible ? ' is-visible' : ''}`}
      aria-labelledby="wh-title"
    >
      <div className="wh-inner">
        <header className="wh-intro">
          <p className="wh-eyebrow">Workflow</p>
          <h2 id="wh-title" className="wh-title">
            How Perfect Predict Works
          </h2>
          <p className="wh-subtitle">
            A structured workflow: choose a strategy, choose direction, then execute.
          </p>
        </header>

        <ol className="wh-timeline">
          {STEPS.map((step, index) => {
            const Illust = step.Illustration
            return (
              <li
                key={step.id}
                className={`wh-card wh-card--${step.id}`}
                style={{ ['--wh-i' as string]: index }}
              >
                <div className="wh-card__index">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="wh-card__title">{step.title}</h3>
                <p className="wh-card__body">{step.body}</p>
                <Illust />
                {index < STEPS.length - 1 ? (
                  <div className="wh-connector" aria-hidden="true">
                    <span className="wh-connector__line" />
                    <span className="wh-connector__arrow" />
                  </div>
                ) : null}
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
